import {
  X, Pencil, Send, Clock, FileText, Building2, Users, Calendar,
  Pin, Eye, Landmark, Megaphone, Check, Layers, User,
} from 'lucide-react';
import type { ComunicadoItem, ComunicadosConfig } from './ComunicadosConfig';
import { ComunicadoPreviewCore, DEFAULT_PREVIEW_VISIBILITY } from './ComunicadoPreviewCore';

interface Props {
  comunicado: ComunicadoItem | null;
  config: ComunicadosConfig;
  onClose: () => void;
  onEdit: (c: ComunicadoItem) => void;
  onSend?: (c: ComunicadoItem) => void;
}

import { DESTINATARIO_LABEL_LONG } from './comunicados-shared';

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  ENVIADO: { label: 'Enviado', color: '#059669' },
  RASCUNHO: { label: 'Rascunho', color: '#6b7280' },
  AGENDADO: { label: 'Agendado', color: '#d97706' },
  CANCELADO: { label: 'Cancelado', color: '#dc2626' },
};

const CAT_MAP: Record<string, { label: string; color: string }> = {
  OPERACIONAL: { label: 'Operacional', color: '#2563eb' },
  INSTITUCIONAL: { label: 'Institucional', color: '#9333ea' },
  PEDAGOGICO: { label: 'Pedagógico', color: '#059669' },
  TEMPLATE: { label: 'Template', color: '#059669' },
};

export function ComunicadoDetailModalCore({
  comunicado: c,
  config,
  onClose,
  onEdit,
  onSend,
}: Props) {
  if (!c) return null;

  const st = STATUS_MAP[c.status] || STATUS_MAP.ENVIADO;
  const cat = CAT_MAP[c.categoria] || { label: c.categoria, color: '#2563eb' };

  const fmt = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return d;
    }
  };

  const midiaUrl = c.configPreview?.midiaUrl || (c as any).midiaUrl;

  return (
    <div className="ccd-overlay" onClick={onClose}>
      <div className="ccd-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ccd-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <Megaphone size={16} style={{ color: cat.color }} />
            <span className="ccd-header-title">Detalhes do Comunicado</span>
            <span
              className="ccd-status-badge"
              style={{
                background: `${st.color}18`,
                color: st.color,
                border: `1px solid ${st.color}30`,
              }}
            >
              {st.label}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="ccd-edit-btn"
              onClick={() => {
                onClose();
                onEdit(c);
              }}
            >
              <Pencil size={12} /> Editar
            </button>
            {c.status === 'RASCUNHO' && onSend && (
              <button
                className="ccd-send-btn"
                onClick={() => {
                  onSend(c);
                  onClose();
                }}
              >
                <Send size={12} /> Enviar
              </button>
            )}
            <button className="ccd-close-btn" onClick={onClose}>
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Conteúdo em 2 Colunas */}
        <div className="ccd-content">
          {/* Lado Esquerdo: Prévia do Card WhatsApp */}
          <div>
            <div className="ccd-section-label">Pré-visualização do Folder / Aviso</div>
            <ComunicadoPreviewCore
              titulo={c.titulo}
              corpo={c.corpo}
              tipo={c.tipo}
              categoria={c.categoria}
              destinatario={c.destinatario}
              midiaUrl={midiaUrl}
              branding={config.branding}
              visibility={
                c.configPreview
                  ? { ...DEFAULT_PREVIEW_VISIBILITY, ...c.configPreview }
                  : DEFAULT_PREVIEW_VISIBILITY
              }
            />
          </div>

          {/* Lado Direito: Metadados e Auditoria */}
          <div>
            <div className="ccd-section-label">Informações de Envio & Engajamento</div>
            <div className="ccd-info-panel">
              {/* KPIs Rápidos */}
              <div className="ccd-kpi-row">
                <div className="ccd-kpi-card">
                  <Eye size={16} color="#2563eb" />
                  <div>
                    <div className="ccd-kpi-val">{c._count?.visualizacoes ?? 0}</div>
                    <div className="ccd-kpi-lbl">Confirmações de Leitura</div>
                  </div>
                </div>
                <div className="ccd-kpi-card">
                  <Users size={16} color="#059669" />
                  <div>
                    <div className="ccd-kpi-val">{DESTINATARIO_LABEL_LONG[c.destinatario] || c.destinatario}</div>
                    <div className="ccd-kpi-lbl">Público Destinatário</div>
                  </div>
                </div>
              </div>

              {/* Tabela de Propriedades */}
              <div className="ccd-props-grid">
                <div className="ccd-prop">
                  <span className="ccd-prop-label">Categoria</span>
                  <span
                    className="ccd-prop-val"
                    style={{ color: cat.color, fontWeight: 700 }}
                  >
                    {cat.label}
                  </span>
                </div>
                <div className="ccd-prop">
                  <span className="ccd-prop-label">Tipo</span>
                  <span className="ccd-prop-val">{c.tipo}</span>
                </div>
                <div className="ccd-prop">
                  <span className="ccd-prop-label">Criado em</span>
                  <span className="ccd-prop-val">{fmt(c.criadoEm)}</span>
                </div>
                {c.agendadoPara && (
                  <div className="ccd-prop">
                    <span className="ccd-prop-label">Agendado para</span>
                    <span className="ccd-prop-val" style={{ color: '#d97706', fontWeight: 700 }}>
                      {fmt(c.agendadoPara)}
                    </span>
                  </div>
                )}
                {c.dataEvento && (
                  <div className="ccd-prop">
                    <span className="ccd-prop-label">Data do Evento</span>
                    <span className="ccd-prop-val">{fmt(c.dataEvento)}</span>
                  </div>
                )}
                {c.turma && (
                  <div className="ccd-prop">
                    <span className="ccd-prop-label">Turma</span>
                    <span className="ccd-prop-val">{c.turma.nome}</span>
                  </div>
                )}
                {c.escola && (
                  <div className="ccd-prop">
                    <span className="ccd-prop-label">Escola</span>
                    <span className="ccd-prop-val">{c.escola.nome}</span>
                  </div>
                )}
                {c.criadoPor && (
                  <div className="ccd-prop">
                    <span className="ccd-prop-label">Autor</span>
                    <span className="ccd-prop-val">
                      {c.criadoPor.nome} ({c.criadoPor.perfil})
                    </span>
                  </div>
                )}
                <div className="ccd-prop">
                  <span className="ccd-prop-label">Fixado no topo</span>
                  <span className="ccd-prop-val">{c.fixado ? 'Sim' : 'Não'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
